import api from './api';

// ==================== TYPES ====================

/**
 * جایگاه‌های هیروی بلاگ — ترتیب آرایه دقیقاً ترتیب نمایش در هیروی سایت است.
 * هر هفت جایگاه دستیِ ادمین‌اند؛ جایگاه خالی در سایت با آخرین مطالب پر می‌شود.
 */
export const BLOG_HERO_POSITIONS = [
  'hero-main',
  'hero-small-1',
  'hero-small-2',
  'hero-bottom',
  'hero-text-1',
  'hero-text-2',
  'hero-text-3',
] as const;

export type BlogHeroPosition = (typeof BLOG_HERO_POSITIONS)[number];

export const BLOG_HERO_POSITION_LABELS: Record<BlogHeroPosition, string> = {
  'hero-main': 'مقاله اصلی',
  'hero-small-1': 'کوچک بالا ۱',
  'hero-small-2': 'کوچک بالا ۲',
  'hero-bottom': 'کوچک پایین',
  'hero-text-1': 'تیتر بدون عکس ۱',
  'hero-text-2': 'تیتر بدون عکس ۲',
  'hero-text-3': 'تیتر بدون عکس ۳',
};

/** توضیح جای دقیق هر جایگاه روی صفحهٔ بلاگ */
export const BLOG_HERO_POSITION_HINTS: Record<BlogHeroPosition, string> = {
  'hero-main': 'تصویر بزرگ سمت راست + تیتر و خلاصه',
  'hero-small-1': 'ردیف بندانگشتی ۱ ستون کناری',
  'hero-small-2': 'ردیف بندانگشتی ۲ ستون کناری',
  'hero-bottom': 'ردیف بندانگشتی ۳ ستون کناری',
  'hero-text-1': 'ردیف فقط‌تیتر ۱ (پایین ستون کناری)',
  'hero-text-2': 'ردیف فقط‌تیتر ۲ (پایین ستون کناری)',
  'hero-text-3': 'ردیف فقط‌تیتر ۳ (پایین ستون کناری)',
};

export interface Article {
  _id: string;
  title: string;
  slug: string;
  shortCode?: string;
  summary?: string;
  content?: Record<string, any>; // Tiptap JSON
  contentHtml?: string;
  featuredImage?: string;
  images?: string[];
  author?: {
    _id: string;
    name: string;
    email?: string;
    profileImage?: string;
    authorSlug?: string;
  };
  categoryId?: {
    _id: string;
    name: string;
    slug: string;
  } | null;
  tags?: Array<{
    _id: string;
    name: string;
    slug: string;
  }>;
  seriesId?: string;
  seriesOrder?: number;
  status: 'draft' | 'pending_review' | 'published' | 'archived';
  publishedAt?: string;
  scheduledAt?: string;
  readingTime?: number;
  viewCount?: number;
  likeCount?: number;
  commentCount?: number;
  bookmarkCount?: number;
  reportCount?: number;
  isFeatured: boolean;
  isPinned: boolean;
  isEditorPick: boolean;
  blogHeroPosition: BlogHeroPosition | null;
  editPending?: boolean;
  currentVersion?: number;
  lastEditedAt?: string;
  // SEO
  metaTitle?: string;
  metaDescription?: string;
  metaKeywords?: string[];
  canonicalUrl?: string;
  // Verification
  verificationInfo?: {
    approvedBy?: { _id: string; name: string } | string;
    rejectedBy?: { _id: string; name: string } | string;
    approvedAt?: string;
    rejectedAt?: string;
    approvalNote?: string;
    rejectionReason?: string;
    revisionNote?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ArticlesResponse {
  docs: Article[];
  totalDocs: number;
  limit: number;
  page: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface EditRequest {
  _id: string;
  // Backend field names
  articleId?: {
    _id: string;
    title: string;
    slug: string;
  };
  createdBy?: {
    _id: string;
    name: string;
    email?: string;
  };
  // Legacy aliases (in case backend transforms)
  article?: {
    _id: string;
    title: string;
    slug: string;
  };
  requestedBy?: {
    _id: string;
    name: string;
    email?: string;
  };
  status: 'pending' | 'approved' | 'rejected' | 'PENDING' | 'APPROVED' | 'REJECTED';
  originalData: Record<string, any>;
  editData: Record<string, any>;
  changedFields: string[];
  changes?: Array<{
    fieldName: string;
    fieldNameFa: string;
    originalValue: any;
    newValue: any;
    hasChanged: boolean;
  }>;
  approvalNote?: string;
  rejectionReason?: string;
  approvedBy?: {
    _id: string;
    name: string;
  };
  rejectedBy?: {
    _id: string;
    name: string;
  };
  reviewedBy?: {
    _id: string;
    name: string;
  };
  reviewedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface EditRequestsResponse {
  docs: EditRequest[];
  totalDocs: number;
  limit: number;
  page: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface ArticleComment {
  _id: string;
  articleId: {
    _id: string;
    title: string;
    slug: string;
  };
  userId: {
    _id: string;
    name: string;
    email?: string;
    profileImage?: string;
  };
  content: string;
  isApproved: boolean;
  isEdited: boolean;
  likeCount: number;
  reportCount: number;
  reportedBy?: Array<{
    userId: string;
    reason: string;
    createdAt: string;
  }>;
  parentId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CommentsResponse {
  docs: ArticleComment[];
  totalDocs: number;
  limit: number;
  page: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

// ==================== ARTICLE MANAGEMENT ====================

/**
 * دریافت لیست مقالات با فیلتر و صفحه‌بندی
 */
export const getArticles = async (
  page: number = 1,
  limit: number = 20,
  filters?: {
    status?: string;
    category?: string;
    search?: string;
    isFeatured?: boolean;
    isPinned?: boolean;
    isEditorPick?: boolean;
  }
): Promise<ArticlesResponse> => {
  const params: Record<string, any> = { page, limit };

  if (filters?.status) params.status = filters.status;
  // بک‌اند این فیلتر را با نام categoryId می‌خواند
  if (filters?.category) params.categoryId = filters.category;
  if (filters?.search) params.search = filters.search;
  if (filters?.isFeatured !== undefined) params.isFeatured = filters.isFeatured;
  if (filters?.isPinned !== undefined) params.isPinned = filters.isPinned;
  if (filters?.isEditorPick !== undefined) params.isEditorPick = filters.isEditorPick;

  const response = await api.get('/admin/articles', { params }) as any;
  return response?.data || response;
};

/**
 * دریافت جزئیات یک مقاله
 */
export const getArticle = async (id: string): Promise<Article> => {
  const response = await api.get(`/admin/articles/${id}`) as any;
  return response?.data || response;
};

/**
 * تایید مقاله
 */
export const approveArticle = async (id: string, approvalNote?: string): Promise<Article> => {
  const response = await api.patch(`/admin/articles/${id}/approve`, {
    approvalNote
  }) as any;
  return response?.data || response;
};

/**
 * رد مقاله
 */
export const rejectArticle = async (id: string, rejectionReason: string): Promise<Article> => {
  const response = await api.patch(`/admin/articles/${id}/reject`, {
    rejectionReason
  }) as any;
  return response?.data || response;
};

/**
 * درخواست بازنگری مقاله
 */
export const requestRevision = async (id: string, revisionNote: string): Promise<Article> => {
  const response = await api.patch(`/admin/articles/${id}/revision-request`, {
    revisionNote
  }) as any;
  return response?.data || response;
};

/**
 * تغییر وضعیت ویژه بودن مقاله
 */
export const toggleFeatured = async (id: string): Promise<Article> => {
  const response = await api.patch(`/admin/articles/${id}/feature`) as any;
  return response?.data || response;
};

/**
 * تغییر وضعیت سنجاق شده مقاله
 */
export const togglePinned = async (id: string): Promise<Article> => {
  const response = await api.patch(`/admin/articles/${id}/pin`) as any;
  return response?.data || response;
};

/**
 * تغییر وضعیت برگزیده سردبیر
 */
export const toggleEditorPick = async (id: string): Promise<Article> => {
  const response = await api.patch(`/admin/articles/${id}/editor-pick`) as any;
  return response?.data || response;
};

/**
 * تنظیم جایگاه هیرو بلاگ
 */
export const setBlogHeroPosition = async (
  id: string,
  position: string | null
): Promise<Article> => {
  const response = await api.patch(`/admin/articles/${id}/hero-position`, {
    position
  }) as any;
  return response?.data || response;
};

/**
 * چیدمان فعلی هیروی بلاگ — همان چیزی که سایت می‌خواند (اندپوینت عمومی).
 * کلید = جایگاه، مقدار = مقالهٔ نشسته در آن جایگاه یا null.
 */
export const getBlogHeroLayout = async (): Promise<Record<string, Article | null>> => {
  const response = await api.get('/articles/hero') as any;
  const data = response?.data?.data || response?.data || response || {};
  return data as Record<string, Article | null>;
};

/**
 * آرشیو کردن مقاله
 */
export const archiveArticle = async (id: string): Promise<Article> => {
  const response = await api.patch(`/admin/articles/${id}/archive`) as any;
  return response?.data || response;
};

/**
 * حذف مقاله
 */
export const deleteArticle = async (id: string): Promise<void> => {
  await api.delete(`/admin/articles/${id}`);
};

// ==================== EDIT REQUESTS ====================

/**
 * دریافت لیست درخواست‌های ویرایش
 */
export const getEditRequests = async (
  page: number = 1,
  limit: number = 20,
  filters?: {
    status?: string;
  }
): Promise<EditRequestsResponse> => {
  const params: Record<string, any> = { page, limit };
  if (filters?.status) params.status = filters.status;

  const response = await api.get('/admin/articles/edit-requests', { params }) as any;
  return response?.data || response;
};

/**
 * دریافت جزئیات یک درخواست ویرایش
 */
export const getEditRequest = async (id: string): Promise<EditRequest> => {
  const response = await api.get(`/admin/articles/edit-requests/${id}`) as any;
  return response?.data || response;
};

/**
 * تایید درخواست ویرایش
 */
export const approveEditRequest = async (id: string, approvalNote?: string): Promise<EditRequest> => {
  const response = await api.post(`/admin/articles/edit-requests/${id}/approve`, {
    approvalNote
  }) as any;
  return response?.data || response;
};

/**
 * رد درخواست ویرایش
 */
export const rejectEditRequest = async (id: string, rejectionReason: string): Promise<EditRequest> => {
  const response = await api.post(`/admin/articles/edit-requests/${id}/reject`, {
    rejectionReason
  }) as any;
  return response?.data || response;
};

// ==================== COMMENT MODERATION ====================

/**
 * دریافت کامنت‌های در انتظار تایید
 */
export const getPendingComments = async (
  page: number = 1,
  limit: number = 20,
  filters?: {
    articleId?: string;
  }
): Promise<CommentsResponse> => {
  const params: Record<string, any> = { page, limit };
  if (filters?.articleId) params.articleId = filters.articleId;

  const response = await api.get('/admin/articles/comments/pending', { params }) as any;
  return response?.data || response;
};

/**
 * دریافت کامنت‌های گزارش‌شده
 */
export const getReportedComments = async (
  page: number = 1,
  limit: number = 20
): Promise<CommentsResponse> => {
  const params = { page, limit };
  const response = await api.get('/admin/articles/comments/reported', { params }) as any;
  return response?.data || response;
};

/**
 * تایید کامنت
 */
export const approveComment = async (id: string): Promise<ArticleComment> => {
  const response = await api.patch(`/admin/articles/comments/${id}/approve`) as any;
  return response?.data || response;
};

/**
 * حذف کامنت
 */
export const deleteComment = async (id: string): Promise<void> => {
  await api.delete(`/admin/articles/comments/${id}`);
};

// ==================== ARTICLE REPORTS ====================

export interface ArticleReportSummary {
  articleId: string;
  articleTitle: string;
  articleSlug: string;
  articleStatus: string;
  authorName: string;
  authorId: string;
  totalReports: number;
  pendingReports: number;
  reasons: string[];
  latestReport: string;
}

export interface ArticleReportSummaryResponse {
  docs: ArticleReportSummary[];
  totalDocs: number;
  page: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface ArticleReportDetail {
  _id: string;
  articleId: string;
  reportedBy: {
    _id: string;
    name: string;
    email?: string;
    profileImage?: string;
  };
  reason: string;
  description?: string;
  status: 'pending' | 'reviewed' | 'action_taken';
  reviewedBy?: { _id: string; name: string };
  reviewedAt?: string;
  adminNote?: string;
  actionTaken?: string;
  createdAt: string;
}

export interface ArticleReportDetailResponse {
  docs: ArticleReportDetail[];
  totalDocs: number;
  page: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

/**
 * دریافت مقالات گزارش‌شده
 */
export const getReportedArticles = async (
  page: number = 1,
  limit: number = 20,
  filters?: { status?: string; reason?: string }
): Promise<ArticleReportSummaryResponse> => {
  const params: Record<string, any> = { page, limit };
  if (filters?.status) params.status = filters.status;
  if (filters?.reason) params.reason = filters.reason;

  const response = await api.get('/admin/articles/reports', { params }) as any;
  return response?.data || response;
};

/**
 * دریافت گزارش‌های یک مقاله
 */
export const getArticleReports = async (
  articleId: string,
  page: number = 1,
  limit: number = 20
): Promise<ArticleReportDetailResponse> => {
  const params = { page, limit };
  const response = await api.get(`/admin/articles/${articleId}/reports`, { params }) as any;
  return response?.data || response;
};

/**
 * بررسی یک گزارش
 */
export const reviewReport = async (
  reportId: string,
  action: 'dismiss' | 'archive' | 'delete',
  adminNote?: string
): Promise<ArticleReportDetail> => {
  const response = await api.patch(`/admin/articles/reports/${reportId}/review`, {
    action,
    adminNote,
  }) as any;
  return response?.data || response;
};

/**
 * بررسی دسته‌جمعی گزارش‌های یک مقاله
 */
export const reviewAllArticleReports = async (
  articleId: string,
  action: 'dismiss' | 'archive' | 'delete',
  adminNote?: string
): Promise<{ modifiedCount: number }> => {
  const response = await api.post(`/admin/articles/${articleId}/reports/review-all`, {
    action,
    adminNote,
  }) as any;
  return response?.data || response;
};

// ==================== HELPERS ====================

export const getStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    draft: 'پیش‌نویس',
    pending_review: 'در انتظار بررسی',
    published: 'منتشرشده',
    archived: 'آرشیو شده',
  };
  return labels[status] || status;
};

export const getStatusColor = (status: string): 'default' | 'warning' | 'success' | 'error' | 'info' => {
  const colors: Record<string, 'default' | 'warning' | 'success' | 'error' | 'info'> = {
    draft: 'default',
    pending_review: 'warning',
    published: 'success',
    archived: 'info',
  };
  return colors[status] || 'default';
};

export const getEditRequestStatusLabel = (status: string): string => {
  const s = status?.toLowerCase();
  const labels: Record<string, string> = {
    pending: 'در انتظار بررسی',
    approved: 'تایید شده',
    rejected: 'رد شده',
  };
  return labels[s] || status;
};

export const getEditRequestStatusColor = (status: string): 'default' | 'warning' | 'success' | 'error' => {
  const s = status?.toLowerCase();
  const colors: Record<string, 'default' | 'warning' | 'success' | 'error'> = {
    pending: 'warning',
    approved: 'success',
    rejected: 'error',
  };
  return colors[s] || 'default';
};

export const getReportReasonLabel = (reason: string): string => {
  const labels: Record<string, string> = {
    inappropriate_content: 'محتوای نامناسب',
    spam: 'اسپم',
    copyright: 'نقض کپی‌رایت',
    misinformation: 'اطلاعات نادرست',
    harassment: 'آزار و اذیت',
    other: 'سایر',
  };
  return labels[reason] || reason;
};

export const getReportStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    pending: 'در انتظار بررسی',
    reviewed: 'بررسی شده',
    action_taken: 'اقدام شده',
  };
  return labels[status] || status;
};

export const getReportStatusColor = (status: string): 'default' | 'warning' | 'success' | 'error' => {
  const colors: Record<string, 'default' | 'warning' | 'success' | 'error'> = {
    pending: 'warning',
    reviewed: 'default',
    action_taken: 'success',
  };
  return colors[status] || 'default';
};
